import { TranslationsContext } from '@teliaee/telia-translate';
import { runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { Card, CardContent, Heading, IModalCoverProps, Modal, ModalContent, scrollToElement, Separator, Text } from 'telia-front-react';
import AddressAutocompleteStore from '../../stores/AddressAutocompleteStore';
import InternetCalculatorStore from '../../stores/InternetCalculatorStore';
import RouteStore from '../../stores/RouteStore';
import SegmentStore from '../../stores/SegmentStore';
import { FetchStatus } from '../../util/FetchStatus';
import customerIdAsKey from '../withCustomerIdAsKey';
import InternetAddressSearch from './InternetAddressSearch';
import InternetCalculatorContent from './InternetCalculatorContent';
import { InternetSpeedTab, InternetSuggestion } from './InternetCalculatorInterfaces';
import OrderFormModalContent from './partner/OrderFormModalContent';

export interface InternetCalculatorProps {
  calculatorKey: string;
  children?: React.ReactNode | string;
  freeRouterInternets?: string[];
  betterInternetFormName?: string;
  betterInternetOnTeliaNetworkFormName?: string;
  betterPartnerFormName?: string;
  specialRequestFormName?: string;
  partnerFormName?: string;
  promotedInternets?: string[];
  excludedWhen5G?: string[];
  internetCodes5G?: string[];
  notices?: React.ReactNode;
  suggestSpeed?: InternetSuggestion[];
  elionOrderPath?: string;
  hideBetterInternetForms?: boolean;
  withoutSurroundingCard?: boolean;
  internetSpeedTabs?: InternetSpeedTab[];
  internetCalculatorStore?: InternetCalculatorStore;
  segmentStore?: SegmentStore;
  routeStore?: RouteStore;
  addressAutocompleteStore?: AddressAutocompleteStore;
  testVariation?: string;
}

const InternetCalculator = (props: InternetCalculatorProps) => {
  const { calculatorKey, children, addressAutocompleteStore, internetCalculatorStore, segmentStore, withoutSurroundingCard, notices } = props;
  const translate = useContext(TranslationsContext).translate;
  const params = props.routeStore.decodedParams;
  let state = internetCalculatorStore.getState(calculatorKey);

  useEffect(() => {
    const {
      betterInternetFormName,
      betterInternetOnTeliaNetworkFormName,
      betterPartnerFormName,
      specialRequestFormName,
      partnerFormName,
      elionOrderPath,
      freeRouterInternets,
      promotedInternets,
      excludedWhen5G,
      internetCodes5G,
      suggestSpeed,
      internetSpeedTabs,
      hideBetterInternetForms,
    } = props;
    internetCalculatorStore.setStateProps(calculatorKey, {
      betterInternetFormName,
      betterInternetOnTeliaNetworkFormName,
      betterPartnerFormName,
      specialRequestFormName,
      partnerFormName,
      elionOrderPath,
      segment: segmentStore.currentSegment || 'PRIVATE',
      freeRouterInternets,
      promotedInternets,
      excludedWhen5G,
      internetCodes5G,
      suggestSpeed,
      internetSpeedTabs,
      hideBetterInternetForms,
    });
    if (params?.addressId && params.buildingId) {
      const address = { id: params.addressId, buildingId: params.buildingId };
      const room = params.roomId ? { roomId: params.roomId } : null;
      internetCalculatorStore
        .setAddress(calculatorKey, address, room, false, segmentStore.currentSegment)
        .then(() => {
          state = internetCalculatorStore.getState(calculatorKey);
          if (state.status === FetchStatus.FETCHED) {
            addressAutocompleteStore.setAddressFieldsByIds(params.addressId, params.buildingId, room);
          }
        })
        .then(() => scrollToElement('addressSearchContainer'))
        .catch(() =>
          runInAction(() => {
            state.status = FetchStatus.ERROR;
          }),
        );
    }
  }, []);

  const modalCoverProps = (): IModalCoverProps => ({
    backgroundColor: '#999999',
    fillImage: true,
    image: {
      srcset: translate('internetAndTvCalculator.elektrileviInternet.orderForm.imageUrl'),
      role: 'presentation',
    },
    minHeight: 500,
  });

  if (!state.calculatorOpen) {
    return null;
  }

  if (state.asButton) {
    return (
      <Modal
        hasContentComponent={true}
        isOpen={state.calculatorOpen}
        onClose={() => {
          internetCalculatorStore.setCalculatorOpen(calculatorKey, false);
          internetCalculatorStore.setOrderFormOpen(calculatorKey, false);
        }}
        cover={state.orderFormOpen ? modalCoverProps() : undefined}
      >
        {state.orderFormOpen ? (
          <ModalContent>
            <OrderFormModalContent calculatorKey={calculatorKey} cmsDataStoreKeys={internetCalculatorStore.getCmsStoreKeys(calculatorKey)} />
          </ModalContent>
        ) : (
          <ModalContent>
            <Text className="text-center">
              <Heading element="h2" className="text__margin-none">
                {translate('internetAndTvCalculator.modal.h2')}
              </Heading>
            </Text>
            <Separator spacing="xsmall" color="invisible" />
            <InternetAddressSearch calculatorKey={calculatorKey} lightSearch={true} />
            <Separator spacing="xxsmall" color="invisible" />
            <InternetCalculatorContent notices={notices} calculatorKey={calculatorKey} children={children} />
          </ModalContent>
        )}
      </Modal>
    );
  }
  if (withoutSurroundingCard) {
    return (
      <div>
        <InternetCalculatorContent notices={notices} calculatorKey={calculatorKey} children={children} />
        <Modal
          hasContentComponent={true}
          isOpen={state.calculatorOpen && state.orderFormOpen}
          onClose={() => internetCalculatorStore.setOrderFormOpen(calculatorKey, false)}
          cover={modalCoverProps()}
        >
          <ModalContent>
            <OrderFormModalContent calculatorKey={calculatorKey} cmsDataStoreKeys={internetCalculatorStore.getCmsStoreKeys(calculatorKey)} />
          </ModalContent>
        </Modal>
      </div>
    );
  }
  return (
    <Card padding="medium">
      <CardContent>
        <Text>
          <Text className="text-center">
            <Heading element="h2" className="text__margin-none">
              {translate('internetAndTvCalculator.modal.h2')}
            </Heading>
          </Text>
          <Separator spacing="xsmall" color="invisible" />
          <InternetCalculatorContent notices={notices} calculatorKey={calculatorKey} children={children} />
          <Modal
            hasContentComponent={true}
            isOpen={state.calculatorOpen && state.orderFormOpen}
            onClose={() => internetCalculatorStore.setOrderFormOpen(calculatorKey, false)}
            cover={modalCoverProps()}
          >
            <ModalContent>
              <OrderFormModalContent calculatorKey={calculatorKey} cmsDataStoreKeys={internetCalculatorStore.getCmsStoreKeys(calculatorKey)} />
            </ModalContent>
          </Modal>
        </Text>
      </CardContent>
    </Card>
  );
};

export default inject('addressAutocompleteStore', 'internetCalculatorStore', 'segmentStore', 'routeStore')(customerIdAsKey(observer(InternetCalculator)));
